import React, { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'

import {
	Box,
	ClickAwayListener,
	Collapse,
	IconButton,
	List,
	ThemeProvider,
	Tooltip,
	Typography,
} from '@mui/material'

import { Grid } from '@mui/material'
import fileSaver from 'file-saver'
import moment, { Moment } from 'moment'
import { ExpiredLicense } from 'ui/expired-license/ExpiredLicense'
import Preloader from 'ui/Preloader'
import { ReactComponent as ArrowCloseDone } from '../assets/arrow_close_done.svg'
import { ReactComponent as ArrowOpenDone } from '../assets/arrow_open_done.svg'
import { ReactComponent as BulletPoints } from '../assets/BulletPoints.svg'
import { ReactComponent as DeviationLogo } from '../assets/deviation_logo.svg'
import doneTasksIcon from '../assets/Donepk.png'
import { ReactComponent as HistoryLogo } from '../assets/history_logo.svg'
import { ReactComponent as LoadFeedLogo } from '../assets/load_feed_logo.svg'
import { ReactComponent as QuestionLogo } from '../assets/question_mark.svg'
import {
	eventHasDeviation,
	isEventWindowClosed,
} from '../components/plan/feedPlanDeviation'
import {
	applyFeedCorrectMock,
	applyFeedWeightMock,
} from '../components/plan/feedWeightMock'
import TestPlanCardView from '../components/plan/TestPlanCardView'
import axAPI from '../httpConfig'
import { selectPermissions } from '../store/authSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchNewFeedPlanData } from '../store/newFeedPlanSlice'
import { COLORS } from '../styles/colors'
import { fontTheme } from '../styles/font'

import DatePicker from 'ui/DatePickerInput/DatePicker'
import { TypographyPrimary } from '../ui/typography/TypographyPrimary'
import { IFeedFact } from '../models/IFeed'
const theme = fontTheme

const factStillNeedsInput = (fact: IFeedFact) =>
	fact.has_fact_data_demand === true &&
	(fact.weight === null || fact.weight === undefined)

const FeedTooltip = props => {
	const [historyColor, setHistoryColor] = React.useState(COLORS.icon)
	const [exportColor, setExportColor] = React.useState(COLORS.icon)
	const permissions = useAppSelector(selectPermissions)
	const handleExportClick = () => {
		axAPI
			.get(`feedplans/${props.value.toISOString().slice(0, 10)}/export`, {
				responseType: 'arraybuffer',
			})
			.then(response => {
				const blob = new Blob([response.data], {
					type: 'application/vnd.ms-excel',
				})
				fileSaver.saveAs(
					blob,
					`План кормления ${moment(props.value).format('DD.MM.YYYY')}.xlsx`,
				)
			})
	}

	return (
		<Box
			display='flex'
			flexDirection='column'
			gap='8px'
			justifyContent='space-between'
			alignItems='flex-start'
		>
			{permissions && permissions.can_export_feedplans && (
				<Box
					display='flex'
					flexDirection='row'
					sx={{ pt: '8px' }}
					alignItems='center'
					onMouseOver={() => setExportColor(COLORS.main)}
					onMouseLeave={() => setExportColor(COLORS.secondaryFont)}
				>
					<LoadFeedLogo
						fill={exportColor}
						stroke={exportColor}
						onMouseOver={() => setExportColor(COLORS.main)}
						onMouseLeave={() => setExportColor(COLORS.secondaryFont)}
					/>
					<Typography
						sx={{
							color: exportColor,
							fontWeight: 400,
							fontSize: '17px',
							lineHeight: '14px',
							cursor: 'pointer',
						}}
						onClick={handleExportClick}
					>
						Экспорт
					</Typography>
				</Box>
			)}
			<NavLink to='history' style={{ padding: '0px' }}>
				<Box
					display='flex'
					flexDirection='row'
					sx={{ pb: '12px' }}
					alignItems='center'
					onMouseOver={() => setHistoryColor(COLORS.main)}
					onMouseLeave={() => setHistoryColor(COLORS.secondaryFont)}
				>
					<HistoryLogo
						stroke={historyColor}
						onMouseOver={() => setHistoryColor(COLORS.main)}
						onMouseLeave={() => setHistoryColor(COLORS.secondaryFont)}
					/>
					<Typography
						sx={{
							color: historyColor,
							fontWeight: 400,
							fontSize: '17px',
							lineHeight: '14px',
							'&:hover': { color: COLORS.main },
						}}
					>
						История изменений
					</Typography>
				</Box>
			</NavLink>
		</Box>
	)
}

const ViewPlanPage = () => {
	const [open, setOpen] = React.useState(false)
	const [listOpen, setListOpen] = React.useState(false)
	const [upcomingOpen, setUpcomingOpen] = React.useState(true)
	const [feedPlanDate, setFeedPlanDate] = React.useState<Date | null>(
		new Date(),
	)
	const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null)

	useLayoutEffect(() => {
		setHeaderSlot(document.getElementById('feed-plan-header-actions'))
	}, [])

	const dispatch = useAppDispatch()

	useEffect(() => {
		setListOpen(false)
		setUpcomingOpen(true)
		dispatch(
			fetchNewFeedPlanData({ date: moment(feedPlanDate).format('YYYY-MM-DD') }),
		)
	}, [feedPlanDate])

	const { feedPlanData, loading, error } = useAppSelector(
		store => store.newFeedPlanReducer,
	)
	if (!feedPlanData) return <></>

	const timeString: string = moment().format('HH:mm:ss')

	const completedDataRation = []
	const upcomingDataRation = []

	applyFeedCorrectMock(applyFeedWeightMock(feedPlanData), feedPlanDate).forEach(
		el => {
			const facts = el.feed_facts ?? []
			const happened = facts.length > 0 && facts.every(fact => fact.fact_dt)
			const windowClosed = isEventWindowClosed(
				el.time,
				feedPlanDate,
				el.deviation,
			)
			if (happened || windowClosed) {
				completedDataRation.push(el)
			} else {
				upcomingDataRation.push(el)
			}
		},
	)
	const currentDate = moment()

	const isPastTime = moment(feedPlanDate).isBefore(currentDate, 'day')
	const isTodayTime = moment(feedPlanDate).isSame(currentDate, 'day')
	const isFutureTime = moment(feedPlanDate).isAfter(currentDate)

	const settingsArea = () => {
		return (
			<Box display='flex' flexDirection='row' alignItems='center'>
				<DatePicker
					value={feedPlanDate}
					onChange={(newDate: Moment | null) =>
						newDate && setFeedPlanDate(newDate.toDate())
					}
					showResetButton={true}
					resetButtonPosition='left'
				/>
				<Box sx={{ pl: '10px' }}>
					<ClickAwayListener onClickAway={() => setOpen(false)}>
						<Box>
							<Tooltip
								placement='bottom-start'
								componentsProps={{
									tooltip: {
										sx: {
											boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
											backgroundColor: 'white',
											borderRadius: '8px',
										},
									},
								}}
								PopperProps={{
									disablePortal: true,
								}}
								onClose={() => setOpen(false)}
								open={open}
								disableFocusListener
								disableHoverListener
								disableTouchListener
								title={<FeedTooltip value={feedPlanDate} />}
							>
								<IconButton
									disableRipple
									sx={{
										color: open ? COLORS.main : COLORS.secondaryFont,
										backgroundColor: 'transparent',
										'&:hover': {
											backgroundColor: 'transparent',
											color: COLORS.main,
										},
									}}
									onClick={() => setOpen(!open)}
								>
									<BulletPoints />
								</IconButton>
							</Tooltip>
						</Box>
					</ClickAwayListener>
				</Box>
			</Box>
		)
	}
	return (
		<ThemeProvider theme={theme}>
			{headerSlot && createPortal(settingsArea(), headerSlot)}
			<Box sx={{ fontFamily: theme.typography.fontFamily, pl: '16px' }}>
				<Grid
					container
					spacing={1}
					columns={{ xs: 6, sm: 6, md: 6, lg: 6 }}
					display='flex'
					flexDirection='column'
					justifyContent='space-between'
				>
					<List sx={{ ml: '8px', pb: '12px' }}>
						{feedPlanData ? (
							<>
								{completedDataRation.length > 0 && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											width: '100%',
											gap: '8px',
											minHeight: '40px',
										}}
									>
										<TypographyPrimary
											sx={{ color: COLORS.secondaryFont, cursor: 'pointer' }}
											onClick={() => {
												setListOpen(!listOpen)
											}}
										>
											Завершенные
										</TypographyPrimary>
										<Typography
											onClick={() => {
												setListOpen(!listOpen)
											}}
											sx={{
												fontSize: '16px',
												lineHeight: '20px',
												fontWeight: '600',
												color: COLORS.secondaryFont,
												cursor: 'pointer',
											}}
										>
											{completedDataRation.length}
										</Typography>

										<Box
											sx={{
												pt: '8px',
												cursor: 'pointer',
											}}
											onClick={() => {
												setListOpen(!listOpen)
											}}
										>
											{listOpen ? <ArrowCloseDone /> : <ArrowOpenDone />}
										</Box>
										{completedDataRation.some(el =>
											eventHasDeviation(el, feedPlanDate),
										) && isFutureTime !== true ? (
											<Box
												sx={{
													backgroundColor: COLORS.deviation,
													borderRadius: '12px',
													p: '4px 9px 4px 9px',
												}}
											>
												<Box
													sx={{
														color: COLORS.deviationFont,
														fontWeight: 400,
														fontSize: '12px',
														lineHeight: '15px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														gap: '4px',
													}}
												>
													<DeviationLogo />
													{`Обнаружено отклонение`}
												</Box>
											</Box>
										) : (
											<></>
										)}
									</Box>
								)}
								<Collapse
									in={listOpen}
									unmountOnExit
									easing={{
										enter: 'cubic-bezier(.42, 0, 1, 1)',
										exit: 'linear',
									}}
								>
									{completedDataRation.map(el => (
										<TestPlanCardView
											{...el}
											isToday={isTodayTime}
											isPast={isPastTime}
											isFuture={isFutureTime}
											feedPlanDate={feedPlanDate}
											timeString={timeString}
											key={el.id}
										/>
									))}
								</Collapse>
							</>
						) : (
							<>
								{feedPlanData.map(el => (
									<TestPlanCardView
										{...el}
										isToday={isTodayTime}
										isPast={isPastTime}
										timeString={timeString}
										feedPlanDate={feedPlanDate}
										isFuture={isFutureTime}
										key={el.id}
									/>
								))}
							</>
						)}
					</List>

					{upcomingDataRation.length > 0 ? (
						<>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									height: '40px',
									pl: '8px',
								}}
							>
								<TypographyPrimary
									sx={{ color: COLORS.secondaryFont, cursor: 'pointer' }}
									onClick={() => setUpcomingOpen(!upcomingOpen)}
								>
									Предстоящие
								</TypographyPrimary>
								<Typography
									onClick={() => setUpcomingOpen(!upcomingOpen)}
									sx={{
										fontSize: '16px',
										lineHeight: '20px',
										fontWeight: '600',
										color: COLORS.secondaryFont,
										cursor: 'pointer',
									}}
								>
									{upcomingDataRation.length}
								</Typography>
								<Box
									sx={{ pt: '8px', cursor: 'pointer' }}
									onClick={() => setUpcomingOpen(!upcomingOpen)}
								>
									{upcomingOpen ? <ArrowCloseDone /> : <ArrowOpenDone />}
								</Box>
								{upcomingDataRation.some(el =>
									eventHasDeviation(el, feedPlanDate),
								) ? (
									<Box
										sx={{
											backgroundColor: COLORS.deviation,
											borderRadius: '12px',
											p: '4px 9px 4px 9px',
										}}
									>
										<Box
											sx={{
												color: COLORS.deviationFont,
												fontWeight: 400,
												fontSize: '12px',
												lineHeight: '15px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												gap: '4px',
											}}
										>
											<DeviationLogo />
											Обнаружено отклонение
										</Box>
									</Box>
								) : (
									<></>
								)}
							</Box>
							<Collapse in={upcomingOpen} unmountOnExit>
								{upcomingDataRation.map(el => (
									<TestPlanCardView
										{...el}
										isToday={isTodayTime}
										isPast={isPastTime}
										timeString={timeString}
										feedPlanDate={feedPlanDate}
										isFuture={isFutureTime}
										key={el.id}
									/>
								))}
							</Collapse>
						</>
					) : (
						<></>
					)}
					{loading ? (
						<Preloader mt={20} />
					) : error?.response?.status === 450 && error?.response?.data ? (
						<ExpiredLicense
							mt={'200px'}
							isShowEmptyData={false}
							availableLicenceDates={
								error.response.data.license_start &&
								error.response.data.license_end
									? `Доступен просмотр данных за период ${moment(
											parseInt(error?.response?.data?.license_start, 10),
										).format('DD.MM.YY')} - ${moment(
											parseInt(error.response.data.license_end),
										).format('DD.MM.YY')}`
									: 'Нет доступных данных по лицензии'
							}
						/>
					) : feedPlanData.length === 0 ? (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								minHeight: 'calc(100vh - 220px)',
								textAlign: 'center',
							}}
						>
							<Typography
								sx={{
									fontSize: '14px',
									lineHeight: '20px',
									fontWeight: 400,
									color: '#7F7F84',
								}}
							>
								План кормления не обнаружен
							</Typography>
							<Typography
								sx={{
									fontSize: '14px',
									lineHeight: '20px',
									fontWeight: 400,
									color: '#7F7F84',
								}}
							>
								Заполните его в{' '}
								<NavLink
									to='/feed/plan/edit'
									style={{
										color: '#5222D0',
										fontSize: '14px',
										fontWeight: 400,
										textDecoration: 'underline',
									}}
								>
									Настройках плана кормления
								</NavLink>
							</Typography>
						</Box>
					) : (
						<>
							{feedPlanData
								.map(el => el.feed_facts.some(factStillNeedsInput))
								.some(el => el === true) &&
							isTodayTime === true &&
							listOpen === false &&
							upcomingDataRation.length === 0 ? (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										paddingTop: '15vh',
									}}
								>
									<QuestionLogo />
									<Typography
										sx={{
											fontSize: '14px',
											lineHeight: '120%',
											fontWeight: '400',
											color: COLORS.secondaryFont,
										}}
									>
										Введите недостающие данные
									</Typography>
								</Box>
							) : (
								<></>
							)}
							{feedPlanData.length > 0 &&
							feedPlanData
								.map(el => el.feed_facts.some(factStillNeedsInput))
								.every(el => el === false) &&
							isTodayTime === true &&
							listOpen === false &&
							upcomingDataRation.length === 0 &&
							completedDataRation.every(el =>
								(el.feed_facts ?? []).every(fact => fact.fact_dt),
							) ? (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '16px',
										minHeight: 'calc(100vh - 280px)',
									}}
								>
									<img src={doneTasksIcon} alt='' width={200} height={200} />
									<Typography
										sx={{
											fontSize: '14px',
											lineHeight: '120%',
											fontWeight: 400,
											color: '#7F7F84',
										}}
									>
										Все задачи на сегодня выполнены
									</Typography>
								</Box>
							) : (
								<></>
							)}
						</>
					)}
				</Grid>
			</Box>
		</ThemeProvider>
	)
}

export default ViewPlanPage
